import {useMemo} from 'react';
import {PERMISSIONS, Permission} from '../types/user';
import {useUserInfo} from './use-user-info';

/**
 * Hook for checking user permissions
 * Optimized version with memoization to reduce unnecessary recalculations
 */
export function usePermissions() {
  const {userInfo, hasPermission, isLoading} = useUserInfo();

  // Memoize individual permission check results
  const isSsoAdminValue = useMemo(
    () => !isLoading && hasPermission(PERMISSIONS.ADMIN_SSOCONFIG),
    [isLoading, hasPermission],
  );

  const canViewComponentsValue = useMemo(
    () => !isLoading && hasPermission(PERMISSIONS.COMPONENT_VIEW),
    [isLoading, hasPermission],
  );

  const canManageCapabilitiesValue = useMemo(
    () => !isLoading && hasPermission(PERMISSIONS.CAPABILITY_MANAGE),
    [isLoading, hasPermission],
  );

  const organizationId = useMemo(() => userInfo?.OrgID ?? '', [userInfo?.OrgID]);

  // Memoize the entire returned object for stable identity
  return useMemo(
    () => ({
      isSsoAdmin: () => isSsoAdminValue,
      getOrganizationId: () => organizationId,
      checkPermission: (permission: Permission) => hasPermission(permission),
      canViewComponents: () => canViewComponentsValue,
      canManageCapabilities: () => canManageCapabilitiesValue,
      isLoading, // Expose loading state
    }),
    [
      isSsoAdminValue,
      canViewComponentsValue,
      canManageCapabilitiesValue,
      organizationId,
      hasPermission,
      isLoading,
    ],
  );
}
